import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import styled from 'styled-components';

import { ApiParams } from '../../../common/api/api';
import { useCollapse } from '../../../common/collapse.hook';
import {
  Product,
  ProductDesignInput,
  ProductDesignOutput,
  ProductEntity,
  ProductUserNeed,
  ProductValidation,
  ProductVerification
} from '../Product.interface';
import {
  useFindProductRelationMap,
  useFindFilteredProductUserNeeds,
  useFindFilteredProductDesignInputs,
  useFindFilteredProductDesignOutputs,
  useFindFilteredProductVerifications,
  useFindFilteredProductValidations
} from '../Product.hooks';
import {
  ProductBoardContext,
  ProductBoardProvider
} from './ProductBoard.context';
import {
  ProductBoardColumnProps,
  ProductBoardColumnEntity
} from './ProductBoardColumn';
import ProductBoardSwimlaneColumns from './ProductBoardSwimlaneColumns';

const StyledContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

function concatUnique(arr1: number[], arr2: number[]): number[] {
  return Array.from(new Set([...arr1, ...arr2]));
}

function filterEntities(
  entities: Partial<ProductEntity>[] | undefined,
  ids: number[]
): Partial<ProductEntity>[] | undefined {
  return entities?.filter(
    (entity: Partial<ProductEntity>) => !ids.includes(entity.id || 0)
  );
}

export interface ProductBoardSwimlaneProps {
  product: Product;
  apiParams?: ApiParams;
  scrollRef?: React.MutableRefObject<HTMLDivElement | null>;
}

const ProductBoardSwimlane: React.FC<ProductBoardSwimlaneProps> = (
  props: ProductBoardSwimlaneProps
) => {
  return (
    <StyledContainer>
      <ProductBoardProvider>
        <ProductBoardSwimlaneRoot {...props} />
      </ProductBoardProvider>
    </StyledContainer>
  );
};

export const ProductBoardSwimlaneRoot: React.FC<ProductBoardSwimlaneProps> = (
  props: ProductBoardSwimlaneProps
) => {
  const { product, apiParams, scrollRef } = props;
  const { selected, entity: selectedEntity } = useContext(ProductBoardContext);

  const { items, allCollapsed, toggle, toggleAll, resetItems } = useCollapse();

  const {
    productUserNeedList,
    findProductUserNeeds
  } = useFindFilteredProductUserNeeds(product.id, selected?.id, selectedEntity);
  const {
    productDesignInputList,
    findProductDesignInputs
  } = useFindFilteredProductDesignInputs(
    product.id,
    selected?.id,
    selectedEntity
  );
  const {
    productDesignOutputList,
    findProductDesignOutputs
  } = useFindFilteredProductDesignOutputs(
    product.id,
    selected?.id,
    selectedEntity
  );
  const {
    productVerificationList,
    findProductVerifications
  } = useFindFilteredProductVerifications(
    product.id,
    selected?.id,
    selectedEntity
  );
  const {
    productValidationList,
    findProductValidations
  } = useFindFilteredProductValidations(
    product.id,
    selected?.id,
    selectedEntity
  );
  const {
    productRelationMap,
    findProductRelationMap
  } = useFindProductRelationMap(product.id);

  // filter out existing ids
  const [designInputIds, setDesignInputIds] = useState<number[]>([]);
  const [designOutputIds, setDesignOutputIds] = useState<number[]>([]);
  const [verificationIds, setVerificationIds] = useState<number[]>([]);
  const [validationIds, setValidationIds] = useState<number[]>([]);

  const params: ApiParams = useMemo(() => {
    return apiParams || {};
  }, [apiParams]);

  const handleIds = useCallback(
    (ids: number[], entity: ProductBoardColumnEntity) => {
      switch (entity) {
        case ProductBoardColumnEntity.DesignInput:
          setDesignInputIds(concatUnique(designInputIds, ids));
          break;
        case ProductBoardColumnEntity.DesignOutput:
          setDesignOutputIds(concatUnique(designOutputIds, ids));
          break;
        case ProductBoardColumnEntity.Verification:
          setVerificationIds(concatUnique(verificationIds, ids));
          break;
        case ProductBoardColumnEntity.Validation:
          setValidationIds(concatUnique(validationIds, ids));
          break;
      }
    },
    [designInputIds, designOutputIds, verificationIds, validationIds]
  );

  const handleFetch = useCallback(
    (entity: ProductBoardColumnEntity) => {
      switch (entity) {
        case ProductBoardColumnEntity.UserNeed:
          findProductUserNeeds(params);
          break;
        case ProductBoardColumnEntity.DesignInput:
          findProductDesignInputs(params);
          break;
        case ProductBoardColumnEntity.DesignOutput:
          findProductDesignOutputs(params);
          break;
        case ProductBoardColumnEntity.Verification:
          findProductVerifications(params);
          break;
        case ProductBoardColumnEntity.Validation:
          findProductValidations(params);
          break;
      }
    },
    [
      params,
      findProductUserNeeds,
      findProductDesignInputs,
      findProductDesignOutputs,
      findProductVerifications,
      findProductValidations
    ]
  );

  useEffect(() => {
    findProductUserNeeds(params);
    findProductDesignInputs(params);
    findProductDesignOutputs(params);
    findProductVerifications(params);
    findProductValidations(params);
  }, [
    params,
    findProductUserNeeds,
    findProductDesignInputs,
    findProductDesignOutputs,
    findProductVerifications,
    findProductValidations
  ]);

  useEffect(() => {
    findProductRelationMap();
  }, [findProductRelationMap]);

  useEffect(() => {
    // reset filtered ids once we get the user needs
    setDesignInputIds([]);
    setDesignOutputIds([]);
    setVerificationIds([]);
    setValidationIds([]);
  }, [productUserNeedList]);

  const userNeeds: Partial<ProductUserNeed>[] | undefined = useMemo(() => {
    if (selected && selectedEntity === ProductBoardColumnEntity.UserNeed) {
      return [selected];
    }
    return productUserNeedList;
  }, [productUserNeedList, selected, selectedEntity]);

  useEffect(() => {
    if (
      productUserNeedList ||
      selectedEntity === ProductBoardColumnEntity.UserNeed
    ) {
      const ids: number[] =
        userNeeds
          ?.filter((userNeed: Partial<ProductUserNeed>) => userNeed.id)
          .map((userNeed: Partial<ProductUserNeed>) => userNeed.id!) || [];
      resetItems(0, ...ids);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productUserNeedList, selectedEntity]);

  // trigger empty UserNeeds every time userNeeds changes.
  // overrides and clears userNeeds in column for filtered items
  const emptyUserNeeds: Partial<ProductUserNeed>[] = useMemo(() => {
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userNeeds]);

  const designInputs:
    | Partial<ProductDesignInput>[]
    | undefined = useMemo(() => {
    return filterEntities(productDesignInputList, designInputIds);
  }, [designInputIds, productDesignInputList]);

  const designOutputs:
    | Partial<ProductDesignOutput>[]
    | undefined = useMemo(() => {
    return filterEntities(productDesignOutputList, designOutputIds);
  }, [designOutputIds, productDesignOutputList]);

  const verifications:
    | Partial<ProductVerification>[]
    | undefined = useMemo(() => {
    return filterEntities(productVerificationList, verificationIds);
  }, [verificationIds, productVerificationList]);

  const validations: Partial<ProductValidation>[] | undefined = useMemo(() => {
    return filterEntities(productValidationList, validationIds);
  }, [validationIds, productValidationList]);

  const hasData: boolean = useMemo(() => {
    return Boolean(
      designInputs?.length ||
        designOutputs?.length ||
        verifications?.length ||
        validations?.length
    );
  }, [designInputs, designOutputs, verifications, validations]);

  return (
    <>
      {userNeeds?.map((userNeed: Partial<ProductUserNeed>, index: number) => {
        const columnProps: Omit<ProductBoardColumnProps, 'entity'> = {
          product,
          apiParams,
          hideHeader: index > 0
        };
        return (
          <ProductBoardSwimlaneColumns
            key={userNeed.id}
            userNeed={userNeed}
            fetchParams={params}
            scrollRef={scrollRef}
            columnProps={columnProps}
            relationMap={productRelationMap}
            handleIds={handleIds}
            handleFetch={handleFetch}
            // stretch if no data for last columns and is last row
            stretch={!hasData && index === userNeeds.length - 1}
            actionsProps={{
              collapsed:
                typeof userNeed.id !== 'undefined' ? items[userNeed.id] : false,
              headerCollapsed: allCollapsed,
              onToggleAll: toggleAll,
              onToggle: () => userNeed.id && toggle(userNeed.id)
            }}
          />
        );
      })}
      {/* used for filtered items */}
      <ProductBoardSwimlaneColumns
        fetchParams={params}
        scrollRef={scrollRef}
        hidden={!hasData && !!userNeeds?.length}
        columnProps={{ product, apiParams, hideHeader: !!userNeeds?.length }}
        relationMap={productRelationMap}
        userNeeds={emptyUserNeeds}
        designInputs={designInputs}
        designOutputs={designOutputs}
        verifications={verifications}
        validations={validations}
        handleFetch={handleFetch}
        actionsProps={{
          collapsed: items[0],
          headerCollapsed: allCollapsed,
          onToggleAll: toggleAll,
          onToggle: () => toggle(0)
        }}
      />
    </>
  );
};

export default ProductBoardSwimlane;
